<!--
  canonical product editor DecorationInspector — Day 4 refactor (audit gaps #9 + #10).

  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
        02-product-editor.md (Day 4 — inspector header + variant grouping).

  Mounted by ContextPanel when selectedLeftRailItem is a placement
  selection. Renders:

    HEADER
      - Numbered chip (matches LeftRail numbering, 1-based by index)
      - Bold placement name
      - Subtitle: "<zone label> · <Technique>"
      - Trash icon (delete via editor.deletePlacement -- immediate persist)

    PLACEMENT DETAILS (Decoration Model Refactor P5 PR-E, Boulders #163)
      - "Decoration #<decoration_id>" reference -- the new model's
        Placement -> Decoration FK, surfaced when present so users can
        see WHICH bound Decoration the placement is using.
      - Dimensions "<W> in x <H> in" sourced from the placement
        payload (imprint_width_in / imprint_height_in are copied
        from decorations_v2.width_in / height_in by the placement
        serializer). Suppressed when both dims are NULL (in-flight
        pending placements not yet sized).
      - Per-color approval badge (P0) -- existing.

    BODY
      - One VariantCard per variant row on the placement (keyed by
        variant id; pending rows key by their tempId). The identical
        auto-created catch_all_light/catch_all_dark pair renders as
        ONE combined card (shade-artwork UX polish -- see the
        combined-shade computeds below).
      - Each card receives routedColorCount + strandedColorCount +
        isPreviewing derived here
      - "Add artwork for specific colors" button below

  Decoration Model Refactor P5 PR-E (Boulders #163,
  https://trello.com/c/S5kN5poU): vocabulary tightening + surface
  the new-shape fields. The existing header subtitle stays in legacy
  form because legacy and new rows both populate
  placement.placement (zone code) and placement.decoration_type
  (method slug) via the reverse-shim. New fields (dimensions +
  bound-Decoration reference) render as a separate detail row so the
  53 existing behavioral tests for the header keep passing.
-->
<template>
    <section
        data-testid="decoration-inspector-root"
        class="deco-inspector-root"
    >
        <template v-if="placement">
            <!--
              Phase 3.5 sweep (P3-Q8): AtomicOrdersNotice surfaces in
              the product editor whenever the user is about to edit a
              Decoration. The same notice is rendered inside the
              Decoration detail page's GovernedEditModal (which Replace
              no longer opens -- it rebinds, see onArtworkPicked).
            -->
            <AtomicOrdersNotice />
            <header class="deco-inspector-header">
                <span
                    data-testid="inspector-header-chip"
                    class="deco-inspector-num"
                    aria-hidden="true"
                >{{ placementNumber }}</span>
                <div class="deco-inspector-header-text">
                    <h3
                        data-testid="inspector-header-name"
                        class="deco-inspector-title"
                    >
                        {{ placementName }}
                    </h3>
                    <p
                        v-if="headerSubtitle"
                        data-testid="inspector-header-subtitle"
                        class="deco-inspector-sub"
                    >
                        {{ headerSubtitle }}
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="inspector-header-trash"
                    class="deco-inspector-trash"
                    aria-label="Delete placement"
                    @click="onDeletePlacement"
                >
                    <span class="material-icons" aria-hidden="true">delete_outline</span>
                </button>
            </header>
            <!--
              Decoration Model Refactor P5 PR-E (Boulders #163):
              Placement details row. Surfaces the new model's
              data shape -- bound Decoration reference + dimensions --
              without disturbing the legacy header subtitle. Renders
              only when at least one of the new fields is present;
              fully suppressed for pre-cutover legacy rows.
            -->
            <div
                v-if="hasPlacementDetails"
                class="deco-inspector-placement-details"
                data-testid="inspector-placement-details"
            >
                <span
                    v-if="boundDecorationLabel"
                    class="deco-inspector-placement-detail"
                    data-testid="inspector-placement-bound-decoration"
                >
                    {{ boundDecorationLabel }}
                </span>
                <span
                    v-if="dimensionsLabel && numericPlacementId == null"
                    class="deco-inspector-placement-detail"
                    data-testid="inspector-placement-dimensions"
                >
                    {{ dimensionsLabel }}
                </span>
                <span
                    v-if="zoneNameLabel"
                    class="deco-inspector-placement-detail"
                    data-testid="inspector-placement-zone-name"
                >
                    {{ zoneNameLabel }}
                </span>
            </div>
            <!--
              The placement's location, changeable in place at any time
              (Jake + Mike, 2026-10-21; card #336 had it only for a
              placement a SKU swap stranded). Saved placements only: an
              unsaved row has nothing to move yet. One location per
              decoration on a product, so another placement's location is
              never offered.
            -->
            <div
                v-if="numericPlacementId != null"
                class="deco-inspector-location"
                data-testid="inspector-placement-location-row"
            >
                <span class="deco-inspector-location-label">Location</span>
                <PlacementLocationSelect
                    class="deco-inspector-location-select"
                    :placement="placement"
                    testid="inspector-placement-location"
                    aria-label="Change location"
                />
            </div>
            <!--
              The placement's size, editable within its location's limits
              (Jake, 2026-10-27). Saved placements only, like the location
              above; an unsaved row shows its size read-only in the details
              row.
            -->
            <div
                v-if="numericPlacementId != null"
                class="deco-inspector-location deco-inspector-size"
                data-testid="inspector-placement-size-row"
            >
                <span class="deco-inspector-location-label">Size</span>
                <PlacementSizeEditor
                    :placement-id="numericPlacementId"
                    :placement="placement"
                />
            </div>
            <!--
              Garment-fit warning (#2445 Track A, Jake's decision
              2026-08-14 "allow with warnings").

              The placement's requested imprint size vs the garment's
              printable area for its zone. Computed SERVER-side
              (services/placement_fit over the existing
              placement_compatibility verdict engine) and delivered on
              the placement payload as `fit_warning`; null = fits, or
              not enough data to judge. We render the server's copy
              verbatim so the wording is single-source.

              ADVISORY (remap): a small resize (<=10%) makes it fit.
              WARNING  (drop):  outside the printable area; per ADR 0043
                                it may need re-approval.

              This is PURELY advisory. It disables nothing and gates
              nothing -- the placement always saves. Do not add a
              blocking behaviour here.
            -->
            <div
                v-if="fitWarning"
                class="deco-inspector-fit-warning"
                :class="`deco-inspector-fit-warning--${fitWarning.severity}`"
                :data-severity="fitWarning.severity"
                data-testid="inspector-placement-fit-warning"
                role="status"
            >
                <v-icon
                    class="deco-inspector-fit-warning-icon"
                    size="16"
                    aria-hidden="true"
                >
                    {{ fitWarningIcon }}
                </v-icon>
                <span
                    class="deco-inspector-fit-warning-text"
                    data-testid="inspector-placement-fit-warning-message"
                >
                    {{ fitWarning.message }}
                </span>
            </div>
            <!--
              Decoration Model Refactor P0 (Boulders #163, locked
              decision #6): per-color approval badge. Default state is
              "Inherits approval" (matches today's behavior). When the
              CSR clicks "Request approval for this color" the badge
              flips to "Pending color-specific approval" and the
              placement carries its own ApprovalRecord. The action is
              shown only when we know which artwork version + client
              the request applies to; the resolver computes those from
              the placement context (placementPerColorContext below).
            -->
            <div
                v-if="placementId != null"
                class="deco-inspector-per-color-badge"
                data-testid="inspector-per-color-badge-mount"
            >
                <PerColorApprovalBadge
                    :placement-id="numericPlacementId"
                    :per-color-approval="placementPerColorApproval"
                    :artwork-version-id="placementArtworkVersionId"
                    :client-id="placementClientId"
                    @request-success="onPerColorRequestSuccess"
                    @request-error="onPerColorRequestError"
                />
            </div>
            <!--
              Variants list + add-artwork CTA.

              Shade-artwork UX polish (docs/design-philosophy.md
              principles 1-3): user-facing copy speaks "artwork", never
              "variant" -- the Variant row is machinery vocabulary.
              Testids/props/events keep the variant name (machinery
              keeps its name; only rendered text changed).

              Combined shade card: the backend auto-creates a
              catch_all_light + catch_all_dark pair on every placement
              bind, both pointing at the SAME artwork
              (api/src/endpoints/decoration_bind.py). Rendering that
              pair as two visually identical cards presents ONE user
              concept as two objects (principle 1). When the pair is
              detected AND the artworks match (including both-null),
              render a single combined card plus a split affordance.
              This is presentation grouping only -- the two underlying
              variant rows are untouched (the schema collapse is a
              separate planned migration). Expansion is local UI state
              and is implied automatically whenever the artworks differ.

              Cards render when present; an empty-state hint renders
              when there are none.
            -->
            <div
                class="deco-inspector-variants"
                data-testid="deco-inspector-variants"
            >
                <template v-if="combinedShadeCard">
                    <VariantCard
                        :key="combinedShadeCard.lightId"
                        :variant-id="combinedShadeCard.lightId"
                        :variant="variantById[combinedShadeCard.lightId]"
                        :artwork="artworkById[combinedShadeCard.lightId] ?? null"
                        :placement-id="placementId"
                        :routed-color-count="combinedShadeRoutedCount"
                        :stranded-color-count="combinedShadeStrandedCount"
                        :is-previewing="combinedShadePreviewing"
                        :lifecycle="placementLifecycle"
                        :product-colors="selectedProductColors"
                        :product-colors-settled="editor.supplierMetaSettled"
                        :colorway-bind-pending="editor.isColorwayBindPending?.(combinedShadeCard.lightId) === true"
                        :combined-shade="true"
                        :colorways="placementColorways"
                        @replace="onReplaceRequested"
                        @send-for-approval="onSendForApprovalRequested"
                        @delete="onDeleteVariant"
                        @set-colorway="onSetVariantColorway"
                        @create-colorway="onCreateColorway"
                        @open-artwork="onOpenArtwork"
                    />
                    <!--
                      Split asks first (Jake, 2026-10-21): which garment colors
                      does the existing decoration go on? SplitShadesDialog
                      below; nothing changes until it is confirmed.
                    -->
                    <button
                        type="button"
                        data-testid="inspector-split-shade-artwork"
                        class="deco-inspector-split-shades"
                        @click="openSplitDialog(combinedShadeCard.lightId)"
                    >
                        Split light / dark
                    </button>
                    <VariantCard
                        v-for="vid in combinedShadeOtherIds"
                        :key="vid"
                        :variant-id="vid"
                        :variant="variantById[vid]"
                        :artwork="artworkById[vid] ?? null"
                        :placement-id="placementId"
                        :routed-color-count="routedCountById[vid] ?? 0"
                        :stranded-color-count="strandedCountById[vid] ?? 0"
                        :is-previewing="previewingVariantId === vid"
                        :lifecycle="placementLifecycle"
                        :product-colors="selectedProductColors"
                        :product-colors-settled="editor.supplierMetaSettled"
                        :colorway-bind-pending="editor.isColorwayBindPending?.(vid) === true"
                        :owned-kinds-by-other="ownedKindsByOtherFor(vid)"
                        :colorways="placementColorways"
                        :stands-for-all-colors="standsForAllColorsFor(vid)"
                        :can-merge-shades="canMergeShadesFor(vid)"
                        :empty-shade="emptyShadeFor(vid)"
                        @replace="onReplaceRequested"
                        @send-for-approval="onSendForApprovalRequested"
                        @delete="onDeleteVariant"
                        @rename="onRenameVariant"
                        @set-kind="onSetVariantKind"
                        @set-colorway="onSetVariantColorway"
                        @create-colorway="onCreateColorway"
                        @split-shades="onSplitShades"
                        @merge-shades="onMergeShades"
                        @open-artwork="onOpenArtwork"
                    />
                </template>
                <template v-else>
                    <VariantCard
                        v-for="vid in variantIds"
                        :key="vid"
                        :variant-id="vid"
                        :variant="variantById[vid]"
                        :artwork="artworkById[vid] ?? null"
                        :placement-id="placementId"
                        :routed-color-count="routedCountById[vid] ?? 0"
                        :stranded-color-count="strandedCountById[vid] ?? 0"
                        :is-previewing="previewingVariantId === vid"
                        :lifecycle="placementLifecycle"
                        :product-colors="selectedProductColors"
                        :product-colors-settled="editor.supplierMetaSettled"
                        :colorway-bind-pending="editor.isColorwayBindPending?.(vid) === true"
                        :owned-kinds-by-other="ownedKindsByOtherFor(vid)"
                        :colorways="placementColorways"
                        :stands-for-all-colors="standsForAllColorsFor(vid)"
                        :can-merge-shades="canMergeShadesFor(vid)"
                        :empty-shade="emptyShadeFor(vid)"
                        @replace="onReplaceRequested"
                        @send-for-approval="onSendForApprovalRequested"
                        @delete="onDeleteVariant"
                        @rename="onRenameVariant"
                        @set-kind="onSetVariantKind"
                        @set-colorway="onSetVariantColorway"
                        @create-colorway="onCreateColorway"
                        @split-shades="onSplitShades"
                        @merge-shades="onMergeShades"
                        @open-artwork="onOpenArtwork"
                    />
                </template>
                <p
                    v-if="!variantIds.length"
                    data-testid="deco-inspector-empty"
                    class="deco-inspector-empty"
                >
                    No artwork on this placement yet.
                </p>
                <!--
                  Merge left an ASSUMED colorway. Names which one the single
                  remaining route is sewing and where to change it, because
                  the merge did not delete anything and the user's other
                  colorways are still there to pick from (deletion is manual,
                  on the decoration detail page).
                -->
                <p
                    v-if="colorwayPairNotice"
                    data-testid="inspector-colorway-pair-notice"
                    class="deco-inspector-merge-notice"
                    role="status"
                >
                    The new thread colors could not be applied to
                    {{ colorwayPairNotice.shade }} garments<template
                        v-if="colorwayPairNotice.keptName"
                    >, which still use &ldquo;{{ colorwayPairNotice.keptName }}&rdquo;</template>.
                    Use Thread colors on that card to try again.
                </p>
                <p
                    v-if="mergeColorwayNotice"
                    data-testid="inspector-merge-colorway-notice"
                    class="deco-inspector-merge-notice"
                >
                    Now using &ldquo;{{ mergeColorwayNotice.name }}&rdquo; for
                    every color. Use Thread colors on the card to pick a
                    different one.
                </p>
            </div>
            <!--
              Mount the SendForApprovalModal for the editor's per-
              variant send flow.
              Original Task #70 (Path B glue) wired this against the
              DecorationArtifact endpoint (PR #1334); Card
              https://trello.com/c/7zSE3lyd re-wires it through the
              Artwork pipeline (Option A) by passing ``artwork-id``
              instead of ``artifact-id``. The modal recognizes the
              ``artworkId`` prop and routes to
              ``/api/artworks/<id>/send-for-approval``.
            -->
            <SendForApprovalModal
                v-if="sendModalOpen"
                v-model="sendModalOpen"
                :artwork-id="sendModalArtworkId"
                :decoration-name="sendModalDecorationName"
                :decoration-version="sendModalDecorationVersion"
                :clients="sendModalClients"
                @sent="onSendComplete"
            />
            <!--
              Editor-local artwork picker for the VariantCard "Replace"
              flow. Two-mode (pick from tenant library | upload new).
              On picked: bind to the variant via productEditor store.
            -->
            <EditorArtworkPicker
                v-if="replaceModalOpen"
                v-model="replaceModalOpen"
                title="Replace artwork"
                :client-id="pickerClientId"
                :in-use-artwork-ids="pickerInUseArtworkIds"
                @picked="onArtworkPicked"
                @close="onReplaceClosed"
            />
            <!--
              Replace = rebind, then ask the scope (Jake, 2026-10-06).
              After a pick on a variant that already has artwork, the user
              chooses "This product only" or "Every product using <old>".
              Both REBIND placements to the picked decoration; neither edits
              a library decoration. (Replace used to route into
              GovernedEditModal against the OLD artwork, which never rebound
              the variant -- the card did not change.)
            -->
            <SplitShadesDialog
                v-if="splitDialog"
                v-model="splitDialogOpen"
                :decoration-name="splitDialog.decorationName"
                :thumbnail-url="splitDialog.thumbnailUrl"
                :ink-hex="splitDialog.inkHex"
                :ink-mixed="splitDialog.inkMixed"
                :shades-declared="splitShadesDeclared"
                :shade-colorway-names="splitShadeColorwayNames"
                @confirm="onSplitConfirm"
                @cancel="onSplitCancel"
            />
            <ReplaceScopeDialog
                v-if="replaceScope"
                v-model="replaceScopeOpen"
                :old-artwork-name="replaceScope.oldName"
                :new-artwork-name="replaceScope.newName"
                :scope="replaceScope.summary"
                :loading="replaceScope.loading"
                :error="replaceScope.error"
                @this-product="onReplaceThisProduct"
                @every-product="onReplaceEveryProduct"
                @cancel="onReplaceScopeCancel"
            />
            <div
                v-if="replaceToastText"
                data-testid="inspector-replace-toast"
                class="deco-inspector-toast"
                role="status"
            >
                {{ replaceToastText }}
                <!--
                  The server sized the new decoration from the NEW artwork
                  (decoration_default_size) and it is not the old size:
                  say so, so a size the user did not type never changes
                  silently (Jake, 2026-10-27).
                -->
                <span
                    v-if="replaceSizeResetText"
                    data-testid="inspector-replace-size-reset"
                >{{ replaceSizeResetText }}</span>
                <!--
                  A placement held because this size of the new design is
                  pinned to an OLDER version: name the remedy and go there.
                -->
                <button
                    v-if="replaceStaleArtworkId"
                    type="button"
                    class="deco-inspector-toast-link"
                    data-testid="inspector-replace-stale-link"
                    @click="openStaleArtwork"
                >
                    Open in the Decoration Library
                </button>
            </div>
            <div
                v-if="sendToastVisible"
                data-testid="inspector-send-toast"
                class="deco-inspector-toast"
                role="status"
            >
                {{ sendToastText }}
            </div>
            <footer
                data-testid="inspector-footer-attribution"
                class="deco-inspector-footer"
            >
                <span
                    data-testid="inspector-footer-attribution-icon"
                    class="material-icons deco-inspector-footer-icon"
                    aria-hidden="true"
                >collections_bookmark</span>
                <span
                    data-testid="inspector-footer-attribution-text"
                    class="deco-inspector-footer-text"
                >
                    All artwork comes from the {{ distributorName }} repository -
                    one source, reusable across this owner's stores.
                </span>
            </footer>
        </template>
    </section>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProductEditorStore } from "@/stores/productEditor";
import { useProductEditorUiState } from "@/stores/productEditorUiState";
import { useEditorArtworkPickerScope } from "./useEditorArtworkPickerScope";
import { useImpersonationStore } from "@/stores/impersonation";
import { useArtworksStore } from "@/stores/artworks";
import api from "@/services/api";
import {
    useVariantResolver,
    resolveVariant,
} from "@/composables/useVariantResolver";
import {
    VARIANT_KIND_CATCH_ALL_LIGHT,
    VARIANT_KIND_CATCH_ALL_DARK,
} from "@/utils/decorationRouting";
import { placementZoneToLabel } from "@/utils/placementZoneToLabel";
import { standsForAllColors } from "@/utils/variantCardLabel";
import { isEmptyShade } from "@/utils/emptyShade";
// The SAME classifier the resolver routes with, so the representative dark
// garment colour we hand the split comes from the very bucket the new dark
// route will serve. A second threshold here would be two answers to one
// question.
import { classifyLDWithName } from "@/utils/luminance";
import VariantCard from "./VariantCard.vue";
import EditorArtworkPicker from "./EditorArtworkPicker.vue";
import ReplaceScopeDialog from "./ReplaceScopeDialog.vue";
import SplitShadesDialog from "./SplitShadesDialog.vue";
import { variantThumbnailUrl } from "./variantThumbnail";
import {
    colorwaysDeclareShades,
    resolveShadeColorwayId,
} from "./defaultColorway";
import { isEmbroiderySource } from "@/utils/artworkUploadContract";
import { formatArtworkName } from "@/utils/formatArtworkName";
import SendForApprovalModal from "@/pages/distributor/decoration-library/SendForApprovalModal.vue";
import AtomicOrdersNotice from "@/components/decoration/AtomicOrdersNotice.vue";
import PerColorApprovalBadge from "@/components/decoration/PerColorApprovalBadge.vue";
import PlacementLocationSelect from "./PlacementLocationSelect.vue";
import PlacementSizeEditor from "./PlacementSizeEditor.vue";
import {
    productLifecycleFor,
    productLifecycleForVariant,
} from "./productLifecycle.js";

const DEFAULT_DISTRIBUTOR_NAME = "Acme Corp";

const editor = useProductEditorStore();
const editorUi = useProductEditorUiState();

// ADR 0015: the picker defaults to the store's client, resolved by one
// shared composable. This was the second of two callsites until #2855
// removed AddSpecificVariantModal (per-SKU specific-colors authoring is
// out of MVP scope); the resolver stays shared so a future callsite
// cannot drift from this one.
const {
    clientId: pickerClientId,
    inUseArtworkIds: pickerInUseArtworkIds,
} = useEditorArtworkPickerScope();
const impersonation = useImpersonationStore();
const artworks = useArtworksStore();
// Navigation for the picker's "create a new colorway" hand-off (the
// decoration library owns the authoring surface).
const router = useRouter();
const route = useRoute();
const { resolve: resolveVariantForColor } = useVariantResolver();

function decoId(d) {
    return d.id ?? d._tempId;
}

const placementIndex = computed(() => {
    if (editorUi.selectedPlacementId == null) return -1;
    const decos = editor.decorations ?? [];
    return decos.findIndex((d) => decoId(d) === editorUi.selectedPlacementId);
});

const placement = computed(() => {
    const idx = placementIndex.value;
    if (idx < 0) return null;
    return (editor.decorations ?? [])[idx] ?? null;
});

const placementId = computed(
    () => (placement.value ? decoId(placement.value) : null),
);

const placementNumber = computed(() => placementIndex.value + 1);

// ---------------------------------------------------------------------------
// Decoration Model Refactor P0 (Boulders #163) -- per-color approval state
// ---------------------------------------------------------------------------
// The badge reads ``placement.per_color_approval`` (``{id, status}``, the
// derived attribute the API serves on the Placement and the productEditor
// store projects) and surfaces the inherits-vs-overridden state. Pending
// rows (tempId only) have no numeric id; the badge action stays hidden
// for them because an unsaved row can't accept an approval request.
//
// ``placementArtworkVersionId`` comes from the productEditor store's
// projection, which copies it off the placement's bound Decoration (the
// API does not serve it on the Placement). It also keys the colorway
// fetch below, so a null here on a persisted placement is a projection
// defect, not a graceful state -- see
// __tests__/DecorationInspector.colorway-wire.spec.js.
//
// ``placementClientId`` is the owning store's client -- the same ADR 0015
// ``store_client_id`` the artwork picker narrows to, and the client the
// store-scoped send-for-approval recipients are drawn from (#2179). A
// store with no client yields null and hides the action: there is no one
// to ask. GH #3833 / __tests__/DecorationInspector.per-color-wire.spec.js.
const numericPlacementId = computed(() => {
    const p = placement.value;
    if (!p || p._staged || p.id == null) return null;
    // Server rows carry the JSON:API string id; the badge wants an int.
    const id = Number(p.id);
    return Number.isInteger(id) && id > 0 ? id : null;
});

const placementPerColorApproval = computed(() => {
    const p = placement.value;
    if (!p) return null;
    return p.per_color_approval || null;
});

const placementArtworkVersionId = computed(() => {
    const p = placement.value;
    if (!p) return null;
    const candidate = p.artwork_version_id ?? p.current_artwork_version_id;
    return typeof candidate === "number" ? candidate : null;
});

const placementClientId = computed(() => {
    const id = Number(pickerClientId.value);
    return pickerClientId.value != null && Number.isInteger(id) && id > 0
        ? id
        : null;
});

function onPerColorRequestSuccess(payload) {
    // The server has minted the override; mirror it into the store so the
    // badge re-renders to its status without a round-trip. Through the
    // store, not onto the projected row, which is rebuilt from serverState.
    if (numericPlacementId.value == null) return;
    editor.setPerColorApproval(numericPlacementId.value, {
        id: payload?.approval_record_id,
        status: payload?.status,
    });
}

function onPerColorRequestError(msg) {
    // Surface as a console message for now; a future task wires the
    // editor's shared error banner. Keep this terse so a smoke run
    // surfaces it via the console-error check.
    // eslint-disable-next-line no-console
    console.error("Per-color approval request failed:", msg);
}

const placementName = computed(() => {
    const p = placement.value;
    if (!p) return "Placement";
    return p.name || p.placement || "Placement";
});

const zoneCode = computed(() => {
    const p = placement.value;
    if (!p) return null;
    // Read the zone SLUG, not the numeric placement PK. After the P6 PR-A
    // rename `placement_id` is the placement primary key -- rendering it as
    // a zone label showed "18" instead of "Left Chest".
    return (
        p.placement || p.placement_zone_slug || p.zone || p.zone_code || null
    );
});

const zoneLabel = computed(() => {
    const code = zoneCode.value;
    if (!code) return "";
    // Only translate uppercase zone codes via the label map; free-text
    // placement strings pass through unchanged.
    if (/^[A-Z_]+$/.test(String(code))) {
        return placementZoneToLabel(code);
    }
    return String(code);
});

function formatTechnique(t) {
    if (!t) return "";
    const s = String(t).trim();
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

const techniqueLabel = computed(() => {
    const p = placement.value;
    if (!p) return "";
    return formatTechnique(p.decoration_type);
});

const headerSubtitle = computed(() => {
    const z = zoneLabel.value;
    const t = techniqueLabel.value;
    // Suppress when zone exactly duplicates the title (legacy seed shape).
    if (z && z === placementName.value && !t) return "";
    if (z && t) return `${z} · ${t}`;
    if (z) return z;
    if (t) return t;
    return "";
});

// PR-N4: iterate variant rows by id (server-row id or pending tempId).
// Earlier iteration was keyed on the legacy variant_key column which
// is now dropped.
function _variantKey(v) {
    if (!v) return null;
    if (v.id != null) return String(v.id);
    if (v._tempId != null) return String(v._tempId);
    return null;
}

const variantsById = computed(() => {
    const list = placement.value?.variants ?? [];
    const byId = {};
    const order = [];
    for (const v of list) {
        const k = _variantKey(v);
        if (!k) continue;
        if (!Object.prototype.hasOwnProperty.call(byId, k)) {
            byId[k] = v;
            order.push(k);
        }
    }
    return { byId, order };
});

const variantIds = computed(() => variantsById.value.order);
const variantById = computed(() => variantsById.value.byId);

// Phase 1-A — resolve the real Artwork record for each variant via
// the artworks Pinia store. VariantCard reads filename, ink_color_hex,
// and version from this when present; falls back to legacy variant
// fields when no match (pre-Phase-F products with no seeded Artwork,
// or an in-flight artworks-store load).
const artworkById = computed(() => {
    const out = {};
    const byId = variantById.value;
    for (const vid of variantIds.value) {
        const v = byId[vid];
        if (!v) {
            out[vid] = null;
            continue;
        }
        out[vid] = artworks.getArtworkForVariant?.(v) ?? null;
    }
    return out;
});

// PR-N2 + PR-N4: count colors per variant via the kind-aware resolver.
// Each (decoration, color_sku) pair resolves to AT MOST one variant;
// the resolver bakes in manual_ld override + the priority order
// (specific_colors > catch_all L/D > single_catch_all). Counts bucket
// by the RESOLVED variant's id. Colors whose resolver returns null
// fall into the "uncovered" bucket and do not increment any card.
const routedCountById = computed(() => {
    const counts = {};
    for (const k of variantIds.value) counts[k] = 0;
    const skus = editor.getEffectiveAttribute?.("selected_color_skus") ?? [];
    if (!placementId.value) return counts;
    for (const sku of skus) {
        const resolved = resolveVariantForColor(placementId.value, sku);
        if (!resolved) continue;
        const key = _variantKey(resolved);
        if (key && Object.prototype.hasOwnProperty.call(counts, key)) {
            counts[key] += 1;
        }
    }
    return counts;
});

// Slice 2 (editable variant cards) -- kind menu support.
//
// ownedKindsByOtherFor(vid): map of catch-all kind -> the header label of
// the OTHER card that already owns it on this placement, EXCLUDING the
// given variant. The kind menu disables an option present here (single
// light + single dark per placement -- the constraint the backend also
// enforces with a 409). Only catch_all_light / catch_all_dark can be
// single-owned; specific_colors is many-per-placement.
function _cardLabelFor(vid) {
    const v = variantById.value[vid];
    if (!v) return "another card";
    const nm = typeof v.name === "string" ? v.name.trim() : "";
    if (nm) return nm;
    const KIND_LABELS = {
        catch_all_light: "the Light card",
        catch_all_dark: "the Dark card",
        single_catch_all: "the All-colors card",
        specific_colors: "a Custom card",
    };
    return KIND_LABELS[v.kind] ?? "another card";
}
function ownedKindsByOtherFor(vid) {
    const owned = {};
    for (const other of variantIds.value) {
        if (other === vid) continue;
        const k = variantById.value[other]?.kind;
        if (k === VARIANT_KIND_CATCH_ALL_LIGHT
            || k === VARIANT_KIND_CATCH_ALL_DARK) {
            owned[k] = _cardLabelFor(other);
        }
    }
    return owned;
}

// -----------------------------------------------------------------------
// Split / merge routes (card https://trello.com/c/vc8U5340)
// -----------------------------------------------------------------------
//
// THE FACT LIVES IN THE SIBLING SET. A catch_all_light with no dark sibling
// IS "all colors" (Jake, 2026-08-29) -- the two-kind model has no third kind
// to record it, and single_catch_all is deliberately unselectable. A card
// sees only its own row, so the inspector derives the fact and passes it
// down. The predicate itself is shared with the decoration-library label
// path (utils/variantCardLabel.js) so the two surfaces cannot drift.

// The placement's variant rows as a plain array, for the sibling-set tests.
const placementVariants = computed(() =>
    variantIds.value.map((vid) => variantById.value[vid]).filter(Boolean),
);

function standsForAllColorsFor(vid) {
    const v = variantById.value[vid];
    if (!v) return false;
    return standsForAllColors(v, placementVariants.value);
}

// Merge is offered on the LIGHT row of a light/dark pair -- the row that
// SURVIVES the merge. Offering it on the dark row would name the object
// being destroyed rather than the intent ("use one artwork for everything").
// An EMPTY SHADE -- orders skip it (utils/emptyShade.js, the editor copy of
// the backend rule). A fact of the sibling set, so derived here.
function emptyShadeFor(vid) {
    const v = variantById.value[vid];
    return isEmptyShade(v, placementVariants.value);
}

function canMergeShadesFor(vid) {
    const v = variantById.value[vid];
    if (v?.kind !== VARIANT_KIND_CATCH_ALL_LIGHT) return false;
    return placementVariants.value.some(
        (other) => other !== v && other?.kind === VARIANT_KIND_CATCH_ALL_DARK,
    );
}

// A representative DARK garment colour for the split's colorway bind. The
// backend resolves match_garment / contrast_auto slots against ONE concrete
// colour; null means "unknown", which it refuses to guess at rather than
// resolving wrongly -- the right outcome, since a thread computed from a
// guessed garment gets sewn.
//
// Generalised to both shades for the split dialog and the combined card's
// colorway pick, which bind each shade against its own garment colours.
const productColorsByShade = computed(() => {
    const out = { light: [], dark: [] };
    for (const row of selectedProductColors.value) {
        if (!row?.hex) continue;
        const { ld } = classifyLDWithName({
            hex: row.hex,
            name: row.name ?? null,
        });
        if (ld === "light" || ld === "dark") out[ld].push(row);
    }
    return out;
});

function representativeHexFor(shade) {
    return productColorsByShade.value[shade]?.[0]?.hex ?? null;
}

const representativeDarkHex = computed(() => representativeHexFor("dark"));

// PR-L3 — currently-selected product colors as enriched {color_sku,
// name, hex} rows for VariantCard's "Routes: ..." subline. Mirrors the
// supplierColors join so VariantCard can resolve human-readable names
// + classify L/D for the catch-all counts. When the supplier meta
// hasn't loaded yet we still emit {color_sku} entries so the subline
// degrades gracefully (catch-all counts go to 0). Cards are told when these
// rows are FINAL via `editor.supplierMetaSettled` (product-colors-settled),
// so a one-shot decision that needs the hexes waits for them.
const selectedProductColors = computed(() => {
    const skus = editor.getEffectiveAttribute?.("selected_color_skus") ?? [];
    const colors = editor.supplierMeta?.colors ?? [];
    const bySku = new Map(colors.map((c) => [c.color_sku, c]));
    return skus.map((sku) => {
        const row = bySku.get(sku);
        if (row) return { color_sku: sku, name: row.name ?? sku, hex: row.hex ?? null };
        return { color_sku: sku, name: sku, hex: null };
    });
});

// -----------------------------------------------------------------------
// Shade-artwork UX polish -- stranded-color count (delete guard).
// -----------------------------------------------------------------------
//
// For each variant (or set of variants -- the combined shade card
// removes its whole pair), count the colors that CURRENTLY route to
// the removed set and would route to NOTHING afterwards. Both the
// before and after resolutions go through the same pure resolver
// (resolveVariant) with the SAME color-row object, so manual_ld /
// color_overrides semantics AND the L/D name-heuristic classify both
// calls identically. VariantCard uses this to name the coverage gap
// concretely in its delete confirm ("12 dark colors will have no
// artwork for this placement") instead of the generic fallback copy.

function _resolverContext() {
    const manualLdRaw = editor.getEffectiveAttribute?.("manual_ld");
    const overridesRaw = editor.getEffectiveAttribute?.("color_overrides");
    return {
        manualLd:
            manualLdRaw && typeof manualLdRaw === "object"
                ? manualLdRaw
                : null,
        overrides:
            overridesRaw && typeof overridesRaw === "object"
                ? overridesRaw
                : null,
    };
}

// Mirror the resolver composable's internal lookupColor: prefer the
// supplierMeta row, synthesize {sku, name: sku, hex: null} on a miss.
// ONE row object per color serves both resolution passes.
function _colorRowsForResolution() {
    const skus = editor.getEffectiveAttribute?.("selected_color_skus") ?? [];
    const colors = editor.supplierMeta?.colors ?? [];
    const bySku = new Map(colors.map((c) => [c.color_sku, c]));
    return skus.map(
        (sku) => bySku.get(sku) ?? { color_sku: sku, name: sku, hex: null },
    );
}

function _strandedCountForRemoval(removedKeys) {
    const variants = placement.value?.variants ?? [];
    if (!variants.length) return 0;
    const { manualLd, overrides } = _resolverContext();
    const remaining = variants.filter(
        (v) => !removedKeys.has(_variantKey(v)),
    );
    let n = 0;
    for (const color of _colorRowsForResolution()) {
        const before = resolveVariant(
            { variants },
            color,
            manualLd,
            overrides,
        );
        if (!before) continue;
        const key = _variantKey(before);
        if (!key || !removedKeys.has(key)) continue;
        const after = resolveVariant(
            { variants: remaining },
            color,
            manualLd,
            overrides,
        );
        if (!after) n += 1;
    }
    return n;
}

const strandedCountById = computed(() => {
    const counts = {};
    for (const k of variantIds.value) {
        counts[k] = placementId.value
            ? _strandedCountForRemoval(new Set([k]))
            : 0;
    }
    return counts;
});

// -----------------------------------------------------------------------
// Shade-artwork UX polish -- combined shade card (identical pair).
// -----------------------------------------------------------------------
//
// Detect the backend's auto-created catch_all_light + catch_all_dark
// pair when both rows point at the same artwork (including both-null,
// the freshly bound state). The pair is one user concept ("this
// artwork, all colors") so it renders as ONE card; a local split
// affordance expands back to the two per-shade cards for editing.
// Expansion is implied automatically whenever the artworks differ
// (identicalShadePair returns null). Sibling variants of other kinds
// (specific_colors etc.) are unaffected and render as usual.
const shadeSplitExpanded = ref(false);

// The pair a combined-card colorway pick is binding right now, or null.
// See identicalShadePair and onSetVariantColorway.
const combinedColorwayBinding = ref(null);

// Set when that pick bound only ONE row of the pair (the other failed twice):
// the pair now sews different colorways, shows as two cards, and this says
// why and what to do.
const colorwayPairNotice = ref(null);

// The split is per-placement view state: selecting another placement
// resets it so its own identical pair comes up collapsed.
watch(placementId, () => {
    shadeSplitExpanded.value = false;
});

const identicalShadePair = computed(() => {
    const byId = variantById.value;
    const ids = variantIds.value;
    const lightIds = ids.filter(
        (k) => byId[k]?.kind === VARIANT_KIND_CATCH_ALL_LIGHT,
    );
    const darkIds = ids.filter(
        (k) => byId[k]?.kind === VARIANT_KIND_CATCH_ALL_DARK,
    );
    if (lightIds.length !== 1 || darkIds.length !== 1) return null;
    const lightArt = byId[lightIds[0]]?.artwork_file_id ?? null;
    const darkArt = byId[darkIds[0]]?.artwork_file_id ?? null;
    const sameArtwork
        = (lightArt == null && darkArt == null)
            || (lightArt != null
                && darkArt != null
                && String(lightArt) === String(darkArt));
    if (!sameArtwork) return null;
    // ...and the same COLORWAY. A pair sewing different threads per shade
    // (a split with light/dark colorways, Jake 2026-10-21) is two different
    // things; one card would hide that and its menu could only name one.
    const lightCw = byId[lightIds[0]]?.colorway_id ?? null;
    const darkCw = byId[darkIds[0]]?.colorway_id ?? null;
    // ...EXCEPT while the combined card's own pick is binding the pair: the
    // two binds land one paid render apart, and in between the rows differ
    // only because the second has not landed yet.
    const binding = combinedColorwayBinding.value;
    const bindingThisPair = binding
        && binding.lightId === lightIds[0]
        && binding.darkId === darkIds[0];
    if (lightCw !== darkCw && !bindingThisPair) return null;
    return { lightId: lightIds[0], darkId: darkIds[0] };
});

const combinedShadeCard = computed(() =>
    shadeSplitExpanded.value ? null : identicalShadePair.value,
);

const combinedShadeOtherIds = computed(() => {
    const pair = combinedShadeCard.value;
    if (!pair) return [];
    return variantIds.value.filter(
        (k) => k !== pair.lightId && k !== pair.darkId,
    );
});

const combinedShadeRoutedCount = computed(() => {
    const pair = combinedShadeCard.value;
    if (!pair) return 0;
    const counts = routedCountById.value;
    return (counts[pair.lightId] ?? 0) + (counts[pair.darkId] ?? 0);
});

const combinedShadePreviewing = computed(() => {
    const pair = combinedShadeCard.value;
    if (!pair) return false;
    return (
        previewingVariantId.value === pair.lightId
        || previewingVariantId.value === pair.darkId
    );
});

// Gate fix round (PR #1915 P1-1): the combined card stands in for BOTH
// rows, so its delete guard must re-resolve with BOTH rows removed --
// otherwise a dark color "saved" by the doomed dark row would hide the
// real coverage gap.
const combinedShadeStrandedCount = computed(() => {
    const pair = combinedShadeCard.value;
    if (!pair) return 0;
    return _strandedCountForRemoval(new Set([pair.lightId, pair.darkId]));
});

// PR-N2 + PR-N4: which variant id the currently-selected color (if
// any) routes to via the kind-aware resolver. Used to highlight the
// matching VariantCard in the inspector list.
const previewingVariantId = computed(() => {
    const sku = editorUi.selectedColorSku;
    if (!sku || !placementId.value) return null;
    const resolved = resolveVariantForColor(placementId.value, sku);
    return _variantKey(resolved);
});

// Phase F PR-4 — real Artwork lifecycle wins over the interim hash.
//
// Per-variant resolution: walk every variant on this placement, ask the
// artworks store whether a real Artwork record matches. If ANY variant
// resolves to a real "in_review"-equivalent state (via
// `productLifecycleForVariant`'s bridge table), the placement chip
// surfaces "in_review". If at least one variant matches and all real
// matches are "approved", the chip is "approved". If NO variant matches
// any Artwork row, fall back to the interim hash rule (every product still
// gets a deterministic in_review surface for the interim build).
//
// Why "any in_review wins": the chip is a placement-level surface, and
// the review banner exists to draw the user's eye to a row that needs
// attention. Hiding an in-review variant behind a sibling approved
// variant would defeat the banner's purpose.
const placementLifecycle = computed(() => {
    const variants = placement.value?.variants ?? [];
    let sawRealMatch = false;
    let anyInReview = false;
    for (const v of variants) {
        const real = artworks.lifecycleStateForVariant?.(v);
        if (real == null) continue;
        sawRealMatch = true;
        if (
            productLifecycleForVariant(v, placement.value, [], artworks)
                === "in_review"
        ) {
            anyInReview = true;
            break;
        }
    }
    if (sawRealMatch) {
        return anyInReview ? "in_review" : "approved";
    }
    // No Artwork records matched any variant: keep the interim hash so the
    // ReviewBanner still has something to render.
    return productLifecycleFor(placement.value, editor.decorations ?? []);
});

// Footer attribution — Phase F PR-4: real Artwork Repository name wins.
//
// Precedence (highest to lowest):
//   1. Repository name resolved from an actual matched Artwork on this
//      placement. The footer says "All artwork comes from the
//      <REPOSITORY> repository" — when we know which repository owns
//      these variants' artwork files, THAT name is the truth.
//   2. SA impersonation-session distributor name (kept for the case
//      where artwork rows haven't loaded yet or this product predates
//      Phase F seeding).
//   3. Forward-compat: a future loader on the editor's serverState
//      that surfaces `distributor_name`.
//   4. "Acme Corp" — the legacy fallback. Used when nothing else
//      resolves (true-SA viewing without impersonation + no Artwork
//      rows + no server-side name).
//
// Day 7 de-hardcode (Item 3 — preserved): pre-Day-7 this read from
// `editor.product?.campaign?.distributor?.name`, but the productEditor
// store has no `product` field. The impersonation store carries the
// human-readable distributor name set when SuperAdmin starts a
// viewing-as session, and survives reloads via localStorage.
const placementRepositoryName = computed(() => {
    const variants = placement.value?.variants ?? [];
    for (const v of variants) {
        const a = artworks.getArtworkForVariant?.(v);
        if (!a) continue;
        const repo = artworks.getRepository?.(a.repository_id);
        if (repo && typeof repo.name === "string" && repo.name.trim()) {
            return repo.name.trim();
        }
    }
    return null;
});

const distributorName = computed(() => {
    const repoName = placementRepositoryName.value;
    if (repoName) return repoName;
    const impName = impersonation.distributorName;
    if (typeof impName === "string" && impName.trim()) return impName.trim();
    // Forward-compat: if a future loader populates
    // serverState.data.attributes.distributor_name (or the
    // included-array distributor entity), prefer it. Today this is
    // always undefined.
    const attrName
        = editor.serverState?.data?.attributes?.distributor_name;
    if (typeof attrName === "string" && attrName.trim()) {
        return attrName.trim();
    }
    return DEFAULT_DISTRIBUTOR_NAME;
});

// ---------------------------------------------------------------------------
// Decoration Model Refactor P5 PR-E (Boulders #163) -- placement details
// ---------------------------------------------------------------------------
//
// Surface the new model's data shape (bound Decoration + dimensions +
// zone name) in a dedicated details row below the header. Sourced from
// the reverse-shim shape so legacy rows show nothing (fields are NULL)
// and new-model rows surface their data without the editor store having
// to learn a new payload.
//
// Field provenance per the placement serializer in
// ``api/src/services/decoration_view.py``:
//   * placement.decoration_id     -- the new model's Placement FK
//                                       to the bound Decoration
//   * placement.imprint_width_in     -- copied from
//                                       decorations_v2.width_in
//   * placement.imprint_height_in    -- copied from
//                                       decorations_v2.height_in
//   * placement.placement_zone       -- nested zone object when the
//                                       caller serialized it; we read
//                                       .name OR .label when present
//
// The shim returns NULL for both dims on legacy rows (the column
// didn't exist) and on in-flight pending placements that haven't been
// sized yet, so we suppress the row entirely in those cases.

const boundDecorationId = computed(() => {
    const p = placement.value;
    if (!p) return null;
    const id = p.decoration_id;
    return typeof id === "number" ? id : null;
});

const boundDecorationLabel = computed(() => {
    const id = boundDecorationId.value;
    if (id == null) return "";
    return `Decoration #${id}`;
});

function _isFiniteNumber(n) {
    return typeof n === "number" && Number.isFinite(n);
}

function _formatInches(n) {
    // Strip trailing zeros for cleaner display (3.50 -> 3.5, 3.00 -> 3).
    // Keep at most 2 decimals; the underlying column is numeric(6,2).
    const rounded = Math.round(n * 100) / 100;
    return Number.isInteger(rounded)
        ? String(rounded)
        : String(rounded).replace(/0+$/, "").replace(/\.$/, "");
}

const dimensionsLabel = computed(() => {
    const p = placement.value;
    if (!p) return "";
    const w = p.imprint_width_in;
    const h = p.imprint_height_in;
    if (!_isFiniteNumber(w) || !_isFiniteNumber(h)) return "";
    return `${_formatInches(w)} in x ${_formatInches(h)} in`;
});

const zoneNameLabel = computed(() => {
    const p = placement.value;
    if (!p) return "";
    // Prefer the nested placement_zone object's name when present (the
    // new model serializes a relationship). Tolerate either .name or
    // .label depending on serializer choice. Fall back to nothing --
    // the legacy header subtitle already carries the zone in
    // zoneLabel form for legacy rows.
    const pz = p.placement_zone;
    if (pz && typeof pz === "object") {
        const candidate = pz.name || pz.label;
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }
    }
    return "";
});

const hasPlacementDetails = computed(() => {
    return Boolean(
        boundDecorationLabel.value
            || (dimensionsLabel.value && numericPlacementId.value == null)
            || zoneNameLabel.value,
    );
});

// ---------------------------------------------------------------------------
// Garment-fit warning (#2445 Track A)
// ---------------------------------------------------------------------------
//
// Jake's decision 2026-08-14: "allow with warnings". When a placement's
// requested imprint size exceeds the garment's printable area for its
// zone, tell the user -- clearly -- and let them save anyway.
//
// The verdict is computed SERVER-side and arrives on the placement
// payload as `fit_warning`:
//
//     {severity, verdict, message, size_delta_pct,
//      zone_max_width_in, zone_max_height_in}
//
// `null` / absent means "fits, or not enough data to judge" -> render
// nothing. Severity is "advisory" (remap: a <=10% resize fits) or
// "warning" (drop: outside the printable area; per ADR 0043 it may need
// re-approval).
//
// Why the math is not here: `api/src/services/placement_fit.py` projects
// the EXISTING `placement_compatibility.compute_verdict` engine, which
// owns the inches comparison against placement_zones maxima, the ADR
// 0065 youth size-scale shrink, and the P4-Q11 10.0% strict-exclusive
// boundary. Re-deriving any of that in JS would fork a rule that is
// already correct and already test-pinned. We render the server's copy
// verbatim so the wording cannot drift between surfaces.
//
// Why this is not an Artifi clamp: the Artifi render request carries NO
// width or height (see the service docstring for the trace), so there
// was never a size to clamp. This is a garment-fit judgment against the
// zone's printable area in inches.
//
// Defensive: a malformed payload (missing severity/message) renders
// nothing rather than an empty banner.
const FIT_SEVERITIES = ["advisory", "warning"];

const fitWarning = computed(() => {
    const raw = placement.value?.fit_warning;
    if (!raw || typeof raw !== "object") return null;
    const severity = raw.severity;
    const message = raw.message;
    if (!FIT_SEVERITIES.includes(severity)) return null;
    if (typeof message !== "string" || !message.trim()) return null;
    return { ...raw, severity, message: message.trim() };
});

const fitWarningIcon = computed(() => {
    // Vuetify's default icon set here is 'md' (ligature names), NOT
    // 'mdi' -- see main.js `defaultSet: 'md'`.
    return fitWarning.value?.severity === "warning"
        ? "warning_amber"
        : "info_outline";
});

async function onDeletePlacement() {
    // Wired to productEditor.deletePlacement, which persists the delete
    // IMMEDIATELY (awaited) -- the DecorationInspector twin of the LeftRail
    // fix (#2454). The optimistic UX is preserved (the placement row
    // disappears), but it is now backed by a real DELETE /api/placements/<id>/
    // rather than a delete merely staged in pendingDecorationDeletes until
    // "Save product". Server ids fire the DELETE; pending (`tmp-*`) rows are
    // dropped locally with no API call. On failure the store rolls back the
    // row and surfaces a saveError, so the UI never silently no-persists.
    if (typeof editor.deletePlacement === "function" && placementId.value != null) {
        await editor.deletePlacement(placementId.value);
    }
}

// -----------------------------------------------------------------------
// Variant artwork replace flow (canonical product editor)
// -----------------------------------------------------------------------
//
// VariantCard emits 'replace' with {variantId, placementId}. We open
// the EditorArtworkPicker, capture the picked Artwork id + filename in
// the 'picked' handler, and route through the productEditor store's
// bindVariantArtwork action. The store handles both pending variants
// (mutate pendingVariants) AND server variants (optimistic patch +
// PATCH /api/variants/:id/).
const replaceModalOpen = ref(false);
const replaceVariantId = ref(null);
// Gate fix round (PR #1915 P1-1): when the replace originates from the
// COMBINED shade card, the action must fan out to both rows of the
// identical pair -- a half-applied replace (light row only) silently
// splits the pair and leaves dark colors on the old artwork. The
// companion is the dark row's bind key; null for single-card replaces.
const replaceCompanionId = ref(null);

function onReplaceRequested(payload) {
    if (!payload || payload.variantId == null) return;
    const v = variantById.value?.[payload.variantId];
    if (!v) return;
    // Use _tempId for pending variants, else the server id. The store
    // dispatches on the same key in bindVariantArtwork.
    replaceVariantId.value = v._tempId ?? v.id ?? null;
    if (replaceVariantId.value == null) return;
    replaceCompanionId.value = null;
    const pair = combinedShadeCard.value;
    if (pair && payload.variantId === pair.lightId) {
        const companion = variantById.value?.[pair.darkId];
        replaceCompanionId.value
            = companion?._tempId ?? companion?.id ?? null;
    }
    replaceModalOpen.value = true;
}

async function onArtworkPicked(picked) {
    // Capture the targets BEFORE anything else. EditorArtworkPicker emits
    // 'picked' and then 'close' synchronously, and the close handler
    // (onReplaceClosed) clears replaceVariantId / replaceCompanionId -- so
    // any read of those refs after an await saw null, and the combined
    // card's companion bind was silently dropped.
    const primaryId = replaceVariantId.value;
    const companionId = replaceCompanionId.value;
    if (!picked || picked.artworkFileId == null || primaryId == null) return;

    const bindPayload = {
        artworkFileId: picked.artworkFileId,
        artworkFilename: picked.artworkFilename ?? null,
        inkColorHex: picked.inkColorHex ?? null,
    };
    const variant = variantById.value?.[primaryId];
    const prior = variant?.artwork_file_id ?? variant?.artwork_id ?? null;
    if (prior != null && String(prior) === String(picked.artworkFileId)) {
        // Picked the decoration that is already bound: nothing to change.
        return;
    }

    // The placement this card belongs to, captured now for the same reason
    // as the variant ids above.
    const targetPlacementId = placementId.value;

    // No prior artwork, a legacy non-numeric storage key that is not an
    // artworks.id, or an unsaved (tmp-) row the server has never seen:
    // there is no saved "old decoration" to scope by, so bind this card
    // locally.
    if (
        prior == null
        || !/^\d+$/.test(String(prior))
        || String(primaryId).startsWith("tmp-")
        || targetPlacementId == null
        || String(targetPlacementId).startsWith("tmp-")
    ) {
        await bindReplaceTargets(primaryId, companionId, bindPayload);
        return;
    }

    const oldArtworkId = String(prior);
    const oldArtwork = artworkById.value?.[primaryId]
        ?? artworks.getArtworkById?.(oldArtworkId)
        ?? null;
    const newArtwork = artworks.getArtworkById?.(picked.artworkFileId) ?? null;
    const cardId = splitCardIdFor(primaryId, companionId, oldArtworkId);
    replaceScope.value = {
        placementId: targetPlacementId,
        variantId: cardId,
        oldArtworkId,
        newArtworkId: String(picked.artworkFileId),
        oldName:
            formatArtworkName(oldArtwork?.filename ?? variant?.artwork_filename)
            || "this decoration",
        newName:
            formatArtworkName(picked.artworkFilename ?? newArtwork?.filename)
            || "the new decoration",
        summary: null,
        loading: true,
        error: null,
    };
    replaceScopeOpen.value = true;
    const request = replaceScope.value;
    try {
        const summary = await editor.fetchArtworkRebindScope(oldArtworkId, {
            newArtworkId: String(picked.artworkFileId),
            placementId: targetPlacementId,
            ...(cardId != null ? { variantId: cardId } : {}),
        });
        if (replaceScope.value === request) {
            replaceScope.value = { ...request, summary, loading: false };
        }
    } catch (err) {
        if (replaceScope.value === request) {
            replaceScope.value = {
                ...request,
                loading: false,
                error: err?.message ?? "Could not load usage.",
            };
        }
    }
}

// Card #307: Replace on ONE card of a split light/dark pair must move only
// that card. A freshly bound pair holds the same artwork on both rows, and a
// placement-wide replace moves every row on the old art -- so replacing the
// Dark card moved the Light row too, the pair was identical again, it
// re-collapsed into one card and the Dark-garment box vanished. Name the
// card whenever ANOTHER row on this placement still binds the old art; the
// combined card (it has a companion) stands for the whole pair and names
// none. Returns the card's server id, or null.
function splitCardIdFor(primaryId, companionId, oldArtworkId) {
    if (companionId != null) return null;
    const shared = placementVariants.value.some((v) => {
        const key = v?._tempId ?? v?.id ?? null;
        return (
            key != null
            && String(key) !== String(primaryId)
            && String(v.artwork_file_id ?? v.artwork_id ?? "") === oldArtworkId
        );
    });
    return shared ? primaryId : null;
}

// The scope dialog's pending replace: {placementId, variantId, oldArtworkId,
// newArtworkId, oldName, newName, summary, loading, error}. Holds its OWN
// copy of the target, independent of replaceVariantId.
const replaceScope = ref(null);
const replaceScopeOpen = ref(false);

// Bind an UNSAVED card locally (nothing on the server to replace yet). On
// the combined light/dark card that is BOTH rows, so the pair stays
// identical (and collapsed).
async function bindReplaceTargets(primaryId, companionId, bindPayload) {
    if (typeof editor.bindVariantArtwork !== "function") return false;
    const errorsBefore = editor.saveErrors?.length ?? 0;
    const refused = () => (editor.saveErrors?.length ?? 0) !== errorsBefore;
    await editor.bindVariantArtwork(primaryId, bindPayload);
    // A refusal of the first row (e.g. the method/format rule) would only
    // repeat on the second; stop so the pair is never half-applied.
    if (companionId != null && !refused()) {
        await editor.bindVariantArtwork(companionId, bindPayload);
    }
    return !refused();
}

async function onReplaceThisProduct() {
    await applyReplace("placement");
}

async function onReplaceEveryProduct() {
    await applyReplace("all");
}

// One server write either way: it re-points the placement's decoration
// (what orders, the decorator and previews read) and moves the variants,
// then the store reloads what was stored. The toast reports the server's
// count, never an assumption.
async function applyReplace(scope) {
    const pending = replaceScope.value;
    replaceScope.value = null;
    if (!pending || typeof editor.replaceArtwork !== "function") return;
    const result = await editor.replaceArtwork({
        oldArtworkId: pending.oldArtworkId,
        newArtworkId: pending.newArtworkId,
        scope,
        placementId: pending.placementId,
        ...(pending.variantId != null ? { variantId: pending.variantId } : {}),
    });
    if (!result) return;
    const n = Number(result.product_count) || 0;
    const held = Number(result.held?.product_count) || 0;
    const stale = (result.held?.stale_version_placement_ids ?? []).length;
    const heldText = `${plural(held, "product")} in live stores keep${
        held === 1 ? "s" : ""} the current decoration until the client `
        + "approves the new one.";
    const parts = [];
    if (n > 0) {
        parts.push(n === 1 ? "Replaced on 1 product." : `Replaced on ${n} products.`);
    }
    if (held > 0) parts.push(n > 0 ? heldText : `Ready for approval: ${heldText}`);
    replaceStaleArtworkId.value = null;
    replaceSizeResetText.value = sizeResetText(result.size_resets);
    if (stale > 0) {
        parts.push(
            `${plural(stale, "placement")} could not change: this design is `
            + "already at that size on an older version. Update this "
            + "decoration to the current version in the Decoration Library "
            + "first, then replace again.",
        );
        replaceStaleArtworkId.value = pending.newArtworkId;
    }
    if (!parts.length) {
        parts.push("Nothing uses that decoration any more, so nothing changed.");
    }
    if (scope === "placement" && n > 0 && held === 0 && stale === 0) {
        parts.splice(0, 1, "Replaced on this product.");
    }
    showReplaceToast(parts.join(" "));
    // Held live stores: the replace prepared the new decoration at each size
    // (unbound). Offer to send it for approval now, through the existing
    // send path, addressed to those stores' clients.
    const heldStoreIds = result.held?.store_ids ?? [];
    if (held > 0 && heldStoreIds.length && (result.held?.decoration_ids ?? []).length) {
        await openSendForNewArtwork(pending, heldStoreIds);
    }
}

function plural(n, noun) {
    return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

// The replace response's `size_resets`: one entry per placement whose new
// decoration is not at its old size, with the size it got and why
// (placement_rebind / decoration_default_size). One size is named; several
// different sizes are counted.
const SIZE_RESET_WHY = {
    aspect_changed: " to fit the new artwork.",
    stitch_file_size: ", the new stitch file's sewn size.",
    aspect_unknown:
        ", the default for this location. Check that the new artwork fits.",
};

function sizeResetText(resets) {
    if (!Array.isArray(resets) || !resets.length) return "";
    const sizes = new Set(resets.map((r) => (r.to ?? []).join("x")));
    const reasons = new Set(resets.map((r) => r.reason));
    if (sizes.size > 1 || reasons.size > 1) {
        return `Size reset on ${plural(resets.length, "placement")} to fit the new artwork.`;
    }
    const [width, height] = resets[0].to ?? [];
    const why = SIZE_RESET_WHY[resets[0].reason] ?? SIZE_RESET_WHY.aspect_changed;
    return `Size reset to ${inches(width)} x ${inches(height)} in${why}`;
}

function inches(value) {
    return String(Number(value));
}

// Live stores need the new art approved first, per size (Jake, option A;
// ADR 0067). After a held replace has prepared the new decoration, open the
// EXISTING send-for-approval modal for the NEW artwork (it sends one record
// per decoration of the current version), addressed to the held stores'
// approval recipients, de-duplicated by client. Nothing on those stores
// changes until the client approves; then the user replaces again.
async function openSendForNewArtwork(pending, storeIds) {
    const byClient = new Map();
    for (const storeId of storeIds) {
        for (const c of await loadRecipientsForStore(storeId)) {
            if (!byClient.has(c.id)) byClient.set(c.id, c);
        }
    }
    const newArtwork = artworks.getArtworkById?.(pending.newArtworkId) ?? null;
    sendModalArtworkId.value = Number(pending.newArtworkId);
    sendModalDecorationName.value = pending.newName;
    sendModalDecorationVersion.value
        = typeof newArtwork?.version === "number" ? newArtwork.version : 1;
    // No card's lifecycle to flip: the new art is not on a card yet.
    sendModalVariantId.value = null;
    sendModalClients.value = [...byClient.values()].sort((a, b) => a.id - b.id);
    sendToastVisible.value = false;
    sendToastText.value = "";
    sendModalOpen.value = true;
}

function onReplaceScopeCancel() {
    replaceScope.value = null;
}

const replaceToastText = ref("");
// The size-reset sentence of the replace notice (empty when no size changed).
const replaceSizeResetText = ref("");
// Set when a replace held a placement on a stale (older-version) size: the
// toast then links to the new artwork in the Decoration Library.
const replaceStaleArtworkId = ref(null);
let replaceToastTimer = null;

function openStaleArtwork() {
    const artworkId = replaceStaleArtworkId.value;
    if (artworkId == null) return;
    router.push({
        name: "library_artwork_detail",
        params: { artwork_id: String(artworkId) },
        query: { returnTo: route.fullPath },
    });
}
function showReplaceToast(text) {
    replaceToastText.value = text;
    if (typeof window === "undefined" || typeof window.setTimeout !== "function") {
        return;
    }
    if (replaceToastTimer) window.clearTimeout(replaceToastTimer);
    replaceToastTimer = window.setTimeout(() => {
        replaceToastText.value = "";
        replaceSizeResetText.value = "";
        replaceStaleArtworkId.value = null;
        replaceToastTimer = null;
    }, replaceStaleArtworkId.value ? 15000 : replaceSizeResetText.value ? 10000 : 6000);
}

function onReplaceClosed() {
    replaceModalOpen.value = false;
    replaceVariantId.value = null;
    replaceCompanionId.value = null;
}

// -----------------------------------------------------------------------
// Variant delete flow — VariantCard emits 'delete' AFTER the inline
// confirm prompt resolves. We dispatch productEditor.deleteVariant
// which calls the backend DELETE endpoint and prunes serverState /
// pendingVariants on success. The inline confirm overlay stays up
// during the request; on success the row drops out of the list (re-
// render) and the overlay goes with it. On failure the store records
// a saveError and resets the variant entry, so we re-fire the
// VariantCard with no confirmingDelete state -- the user sees the
// banner + the unmutated card.
// -----------------------------------------------------------------------
async function onDeleteVariant(payload) {
    if (!payload || payload.variantId == null) return;
    const decoId = placementId.value;
    if (decoId == null) return;
    if (typeof editor.deleteVariant !== "function") return;
    // Combined-card fan-out (P1-1): deleting the combined card means
    // deleting the placement's artwork for ALL colors, i.e. BOTH rows
    // of the identical pair. Sequential dispatches through the
    // existing store action; if the first fails the store's saveError
    // path surfaces it and we stop (no compounding). If the second
    // fails, the surviving dark row renders as its own card (the pair
    // is no longer complete) alongside the store's error banner --
    // the partial state is visible, not hidden.
    const pair = combinedShadeCard.value;
    const targets = [payload.variantId];
    if (pair && payload.variantId === pair.lightId) {
        targets.push(pair.darkId);
    }
    for (const variantId of targets) {
        const ok = await editor.deleteVariant({
            decorationId: decoId,
            variantId,
        });
        if (ok === false) break;
    }
}

// Slice 1 (editable variant cards): commit an inline variant rename.
// VariantCard emits 'rename' with {variantId, placementId, name} on
// Enter/blur/empty-clear; we hand it to the productEditor store's
// renameVariant action (optimistic + rollback + error banner live in
// the store). Combined-shade stand-in cards do not emit rename (the
// component blocks it), so there is no pair fan-out here -- a rename is
// always a single-variant edit.
async function onRenameVariant(payload) {
    if (!payload || payload.variantId == null) return;
    const decoId = placementId.value;
    if (decoId == null) return;
    if (typeof editor.renameVariant !== "function") return;
    await editor.renameVariant({
        decorationId: decoId,
        variantId: payload.variantId,
        name: payload.name ?? null,
    });
}

// Slice 2 (editable variant cards) -- kind menu.
// VariantCard emits 'set-kind' with {variantId, placementId, kind,
// colorSkus?} when the user picks a routing role from the card menu. We
// hand it to the productEditor store's setVariantKind action (optimistic
// + rollback + error banner live in the store). Combined-shade stand-in
// cards do not render the menu, so there is no pair fan-out here.
async function onSetVariantKind(payload) {
    if (!payload || payload.variantId == null || !payload.kind) return;
    const decoId = placementId.value;
    if (decoId == null) return;
    if (typeof editor.setVariantKind !== "function") return;
    await editor.setVariantKind({
        decorationId: decoId,
        variantId: payload.variantId,
        kind: payload.kind,
        colorSkus: payload.colorSkus,
    });
}

// -----------------------------------------------------------------------
// Split / merge routes -- dispatch
// -----------------------------------------------------------------------
//
// The colorway list is passed IN rather than re-fetched: the inspector
// already holds it (one fetch per placement, see placementColorways below),
// and the store action needs it to auto-select the new/surviving route's
// threads. Handing the store a list it cannot see is the whole reason these
// are inspector handlers rather than card-to-store calls.

//
// SPLIT ASKS FIRST (Jake, 2026-10-21): "pop a dialog asking which colors
// (light/dark) the existing decoration should be applied to, and display the
// decoration in the dialog". Both split affordances -- the combined card's
// and a lone light card's -- open SplitShadesDialog; nothing is written until
// it is confirmed, so Cancel leaves the placement unsplit.

// The open dialog's subject, or null. Snapshotted when it opens so the
// dialog keeps describing the decoration the user asked about.
const splitDialog = ref(null);
const splitDialogOpen = ref(false);

function openSplitDialog(variantId) {
    const variant = variantById.value?.[variantId];
    if (!variant) return;
    const art = artworkById.value?.[variantId] ?? null;
    splitDialog.value = {
        variantId,
        decorationName:
            formatArtworkName(art?.filename ?? variant.artwork_filename)
            || "This decoration",
        thumbnailUrl: variantThumbnailUrl(variant, art),
        inkHex: art?.dominant_ink_hex ?? null,
        inkMixed: art?.dominant_ink_mixed === true,
        isEmbroidery: isEmbroiderySource(
            art?.original_filename ?? art?.filename ?? variant.artwork_filename,
        ),
        lightColorwayId: variant.colorway_id ?? null,
    };
    splitDialogOpen.value = true;
}

function onSplitShades(payload) {
    if (!payload || payload.variantId == null) return;
    openSplitDialog(payload.variantId);
}

// Embroidery whose colorways are flagged per shade: the dialog suggests
// both, each with its matching colorway.
const splitShadesDeclared = computed(
    () =>
        splitDialog.value?.isEmbroidery === true
        && colorwaysDeclareShades(placementColorways.value),
);

function splitShadeContext() {
    return {
        light: {
            garmentColors: productColorsByShade.value.light,
            garmentHex: representativeHexFor("light"),
        },
        dark: {
            garmentColors: productColorsByShade.value.dark,
            garmentHex: representativeHexFor("dark"),
        },
    };
}

// What each shade will sew after the split -- the SAME resolution the store
// action binds with, so the dialog names what actually happens.
const splitShadeColorwayNames = computed(() => {
    const out = { light: null, dark: null };
    if (!splitDialog.value?.isEmbroidery) return out;
    const list = placementColorways.value;
    const ctx = splitShadeContext();
    for (const shade of ["light", "dark"]) {
        const id = resolveShadeColorwayId({
            shade,
            colorways: list,
            garmentColors: ctx[shade].garmentColors,
            fallbackId: splitDialog.value.lightColorwayId,
        });
        const row = id == null ? null : list.find((c) => c.id === id);
        out[shade] = row ? row.name || `Colorway ${row.id}` : null;
    }
    return out;
});

async function onSplitConfirm(shades) {
    splitDialog.value = null;
    const decoId = placementId.value;
    if (decoId == null) return;
    if (typeof editor.splitPlacementShades !== "function") return;
    mergeColorwayNotice.value = null;
    const res = await editor.splitPlacementShades({
        decorationId: decoId,
        shades,
        colorways: placementColorways.value,
        shadeContext: splitShadeContext(),
    });
    // Show the two shade cards even when both carry the same decoration and
    // threads -- the user just asked to see them separately.
    if (res?.ok && placementId.value === decoId) shadeSplitExpanded.value = true;
}

function onSplitCancel() {
    splitDialog.value = null;
}

watch(placementId, () => {
    colorwayPairNotice.value = null;
    splitDialog.value = null;
    splitDialogOpen.value = false;
});

// The notice a merge leaves behind when it had to ASSUME a colorway.
//
// Jake: "If I merge back to one route for all colors I still need to be able
// to choose or edit the colorway" -- so when no colorway declares itself for
// all substrates, the survivor keeps the one it was already sewing and we say
// WHICH one and what to click. Shown only for the assumption; a declared
// all-substrate colorway is an answer, and narrating a correct choice back at
// the user is noise.
const mergeColorwayNotice = ref(null);

// Clear the notice when the user moves to another placement -- it describes
// a state on the placement it was raised for, and would misinform elsewhere.
watch(placementId, () => {
    mergeColorwayNotice.value = null;
});

async function onMergeShades(payload) {
    if (!payload || payload.variantId == null) return;
    const decoId = placementId.value;
    if (decoId == null) return;
    if (typeof editor.mergeVariantShades !== "function") return;
    mergeColorwayNotice.value = null;
    const res = await editor.mergeVariantShades({
        decorationId: decoId,
        variantId: payload.variantId,
        colorways: placementColorways.value,
        garmentHex: representativeDarkHex.value,
    });
    if (res?.ok && res.colorwayWasAssumed) {
        mergeColorwayNotice.value = {
            // Fall back to the id when the row is not in the fetched list --
            // naming something is what makes the notice actionable, and a
            // vague "a colorway" would leave the user hunting.
            name: res.colorwayName || `colorway ${res.colorwayId}`,
        };
    }
}

// -----------------------------------------------------------------------
// Colorway binding -- fetch the pick list, forward the bind
// -----------------------------------------------------------------------
//
// Card: Embroidery colorway editing (MUST-SHIP) https://trello.com/c/2hcgDM7J
//
// FETCHED ONCE PER PLACEMENT, NOT PER CARD. Every variant on a placement
// shares one artwork version (``Decoration.artwork_version_id`` is NOT NULL
// and IMMUTABLE), so the colorway list is a property of the placement. Doing
// it per card would issue N identical requests for one answer.
//
// FAILS QUIET. An empty list simply hides the control on every card, which is
// also the correct rendering for the common case: most placements are not
// embroidery and legitimately have no colorways. Surfacing an error banner for
// "this decoration has no colorways" would cry wolf on the majority path.
const placementColorways = ref([]);

// STALE-RESPONSE GUARD. Switching placements fires a second fetch while the
// first may still be in flight, and responses are not ordered -- so a slow
// answer for the PREVIOUS placement can land after the current one's and
// overwrite it.
//
// That matters more than a typical read race. A colorway is an ORDERED
// stop->thread list, so one authored against a different design re-interprets
// stop N as a different object. The backend does refuse such a bind
// (post_set_colorway re-checks colorway.artwork_version_id against the
// placement's decoration), so nothing wrong is ever SEWN -- but the user
// would be picking from a menu of options that silently cannot work, and the
// refusal is a closed-oracle 404 that explains nothing. Do not offer the
// choice.
//
// A monotonic token rather than an AbortController: we only need to decide
// whether a settled response is still wanted, and this stays correct even if
// a response was already in the microtask queue when the switch happened.
let colorwayFetchToken = 0;

watch(
    placementArtworkVersionId,
    async (versionId) => {
        const token = ++colorwayFetchToken;
        placementColorways.value = [];
        if (versionId == null) return;
        try {
            const res = await api.get(
                `/wilcom/artwork-versions/${versionId}/colorways`,
            );
            if (token !== colorwayFetchToken) return;
            const list = res?.data?.colorways;
            placementColorways.value = Array.isArray(list) ? list : [];
        } catch (err) {
            // See FAILS QUIET above. The control hides; nothing is claimed.
            if (token !== colorwayFetchToken) return;
            placementColorways.value = [];
        }
    },
    { immediate: true },
);

// VariantCard emits 'set-colorway' with {variantId, placementId, colorwayId,
// stopCount, garmentHex}. Hand it to the store, which owns the wire contract
// and the error banner; the card stays dumb.
//
// NOT fanned out to a shade PAIR. A colorway resolves against the garment
// colour, so the light and dark cards of a pair legitimately resolve the same
// colorway to DIFFERENT threads -- binding both from one click would either
// send one card's garment colour for both, or spend two paid Artifi renders
// the user did not ask for. Combined-shade cards therefore hide the control
// (VariantCard.showColorwayControl) rather than binding both halves here.
//
// EXCEPT the combined card (Jake, 2026-10-21: "the color cards on the right
// should have a drop-menu that lets me choose colorways"). It stands for one
// decoration on EVERY color, so its pick is a deliberate "this colorway for
// all colors": both rows are bound, each resolved against its OWN shade's
// garment colour. A row already sewing the pick is skipped -- each bind buys
// a paid render.
async function onSetVariantColorway(payload) {
    if (!payload || payload.variantId == null) return;
    if (!Number.isInteger(payload.colorwayId)) return;
    const decoId = placementId.value;
    if (decoId == null) return;
    if (typeof editor.setVariantColorway !== "function") return;
    const pair = combinedShadeCard.value;
    if (pair && payload.variantId === pair.lightId) {
        // Hold the pair together for the whole operation (identicalShadePair),
        // so the card does not break apart between the two renders.
        combinedColorwayBinding.value = { lightId: pair.lightId, darkId: pair.darkId };
        colorwayPairNotice.value = null;
        const bound = [];
        const failed = [];
        try {
            for (const [shade, vid] of [["light", pair.lightId], ["dark", pair.darkId]]) {
                if ((variantById.value?.[vid]?.colorway_id ?? null) === payload.colorwayId) {
                    continue;
                }
                const args = {
                    decorationId: decoId,
                    variantId: vid,
                    colorwayId: payload.colorwayId,
                    stopCount: payload.stopCount,
                    garmentHex: representativeHexFor(shade),
                };
                // One retry. A REFUSAL (4xx) is atomic server-side -- nothing
                // changed -- so trying again cannot double-apply. A thrown
                // timeout or 5xx is different: the bind may have committed
                // (and bought its render) before the response was lost. The
                // retry then binds the same colorway again, which is the same
                // end state at the cost of one more render -- accepted over
                // leaving the pair silently half-bound.
                const errors = Array.isArray(editor.saveErrors) ? editor.saveErrors : null;
                const mark = errors ? errors.length : 0;
                let ok = await editor.setVariantColorway(args);
                if (!ok) {
                    const firstFailureBanner = errors && errors.length > mark;
                    ok = await editor.setVariantColorway(args);
                    // One failure, one banner: drop the first attempt's entry.
                    // If the retry also failed it has pushed its own.
                    if (firstFailureBanner) errors.splice(mark, 1);
                }
                (ok ? bound : failed).push(shade);
            }
        } finally {
            combinedColorwayBinding.value = null;
        }
        // Half applied: the rows now differ, so the pair shows as the two
        // cards it really is -- and the user is told which shade missed and
        // what it still sews. (Both failing changes nothing; the store's
        // error banner covers that.)
        if (bound.length && failed.length) {
            const missed = failed[0];
            const vid = missed === "light" ? pair.lightId : pair.darkId;
            const keptId = variantById.value?.[vid]?.colorway_id ?? null;
            const kept = keptId == null
                ? null
                : placementColorways.value.find((c) => c.id === keptId);
            colorwayPairNotice.value = {
                shade: missed,
                keptName: kept ? kept.name || `Colorway ${kept.id}` : null,
            };
        }
        return;
    }
    await editor.setVariantColorway({
        decorationId: decoId,
        variantId: payload.variantId,
        colorwayId: payload.colorwayId,
        stopCount: payload.stopCount,
        garmentHex: payload.garmentHex ?? null,
    });
}

// "Create a new colorway" from a card's picker. The card knows which variant
// asked; only the inspector can resolve that variant to an ARTWORK, which is
// what the colorway editor's route is keyed by.
//
// Navigates to the EXISTING editor rather than authoring here
// (design-philosophy principle 2). Authoring means assigning a thread to
// every stop in the stitch file -- ColorwayEditor already does that, against
// the only create endpoint there is
// (POST /api/wilcom/artwork-versions/<id>/colorways). A second author-a-
// colorway surface on this card would answer the same product question twice
// and drift from the first the moment either grows a field.
//
// The return path is not a nicety. Authoring happens on a different route, so
// without it the user lands in the decoration library having lost the store,
// product and placement they were configuring -- which would undo the reason
// the picker was put on the routing card in the first place.
//
// No resolvable artwork -> no navigation (principle 5, fail toward the
// record). There is no version to author a colorway against, and routing to
// `/artwork/undefined/colorways` would strand the user on a broken page
// having already left the editor.
// The decoration name on a card opens it in the library (Jake, 2026-10-21:
// "click the name of the decoration to go to it in the decoration library").
// Same tab and the same return path as "Open in the Decoration Library" and
// "Create a new colorway" here, so the user can get back to this placement.
function onOpenArtwork(payload) {
    const artworkId = payload?.artworkId;
    if (artworkId == null) return;
    router.push({
        name: "library_artwork_detail",
        params: { artwork_id: String(artworkId) },
        query: { returnTo: route.fullPath },
    });
}

function onCreateColorway(payload) {
    if (!payload || payload.variantId == null) return;
    const art = artworkById.value?.[payload.variantId];
    const artworkId = art?.id ?? null;
    if (artworkId == null) return;
    router.push({
        name: "library_artwork_colorways",
        params: { artwork_id: artworkId },
        query: { returnTo: route.fullPath },
    });
}

// -----------------------------------------------------------------------
// Task #70 (Path B glue): Send-for-approval wireup
// -----------------------------------------------------------------------
//
// VariantCard renders a "Send for approval" button when its variant has
// an artwork_file_id (= preview rendered). On click it emits
// 'send-for-approval' with {artifactId, variantId, placementId,
// decorationName}. We host the SendForApprovalModal here (alongside the
// inspector that owns variant context) rather than the editor root, so
// only one modal instance ever mounts and it closes naturally when the
// user changes placements.
//
// The modal contract (PR #1334) wants: artifactId, decorationName,
// decorationVersion, clients[]. We resolve the artwork version from the
// resolved Artwork record (artworkById[vid]) when available; fall back
// to v1 to match VariantCard's fallback. Clients come from the Vuex
// store via jv/get clients — the same dispatch DecorationLibraryList
// uses.
const sendModalOpen = ref(false);
// Option A (https://trello.com/c/7zSE3lyd): the product editor's
// variants live in the Artwork pipeline, so the modal's submit hits
// /api/artworks/<artworkId>/send-for-approval (not the
// DecorationArtifact endpoint, which is a different state machine).
// ``sendModalArtworkId`` carries the resolved Artwork.id; we resolve
// it from ``Variant.artwork_file_id`` at click time. The integer-
// string shape (str(Artwork.id)) is the forward path and covers every
// variant the product editor creates. UUID-shape (legacy ArtifiAsset
// bridge) variants are NOT supported by this surface today; clicking
// Send-for-approval on one is rejected client-side with a friendly
// error rather than POSTing a malformed id.
const sendModalArtworkId = ref(null);
const sendModalDecorationName = ref("");
const sendModalDecorationVersion = ref(1);
const sendModalClients = ref([]);
// Issue #2179 Problem 1: remember which variant the open send modal
// targets so onSendComplete can flip its artwork lifecycle to
// awaiting_approval (card re-renders "Send reminder" without a reload).
const sendModalVariantId = ref(null);
const sendToastVisible = ref(false);
const sendToastText = ref("");

// Issue #2179 Problem 2 (tenant isolation): the send modal used to list
// the GLOBAL client roster (vuex ``jv/get clients``), letting an Owner
// send one store's artwork to a client attached to a DIFFERENT store.
// We now load recipients from the store-scoped endpoint
// ``GET /api/stores/<store_id>/approval-recipients`` -- only the
// client(s) attached to THIS store, each carrying its team members. The
// store id comes from the loaded campaign_product's attributes. Fail
// CLOSED: any error (or a missing store id) yields an empty list, NEVER
// a fallback to the global roster.
function _resolveStoreId() {
    const attrs = editor.serverState?.data?.attributes;
    if (!attrs) return null;
    const raw = attrs.store_id ?? attrs.campaign_id ?? null;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
}

async function loadClientsForModal() {
    return loadRecipientsForStore(_resolveStoreId());
}

async function loadRecipientsForStore(storeId) {
    if (storeId == null) return [];
    try {
        const res = await api.get(`/stores/${storeId}/approval-recipients`);
        const list = Array.isArray(res?.data) ? res.data : [];
        // The endpoint already emits the modal's expected shape
        // ({id, name, primary_contact_email}) plus a ``team_members``
        // list (owner contact + client-tier accounts) the modal offers
        // as alternate recipient emails. Pass through defensively.
        return list.map((c) => ({
            id: c.id,
            name: c.name,
            primary_contact_email: c.primary_contact_email ?? null,
            team_members: Array.isArray(c.team_members)
                ? c.team_members
                : [],
        }));
    } catch (err) {
        // Fail closed -- with no scoped recipients the modal renders its
        // empty-state. Do NOT fall back to the global client list.
        return [];
    }
}

// VariantCard's artwork_file_id may be either str(Artwork.id) or a
// legacy ArtifiAsset UUID. Only the integer shape resolves to an
// Artwork row that ApprovalService.send can target. This helper
// returns the Artwork.id as a Number or ``null`` when the shape isn't
// resolvable here.
//
// Why an integer-only client-side resolver?
// =========================================
//
// The Phase F upload pipeline stores ``str(Artwork.id)`` on every new
// Variant.artwork_file_id (see ``api/src/services/artwork_service.py``
// + ``api/src/endpoints/artifi_preview.py:_classify_artwork_file_id``).
// Every variant the product editor creates today is the int-string
// shape. Legacy UUID-shape variants exist in the DecorationArtifact
// pipeline -- they have a separate Send-for-approval endpoint
// (``/api/decoration-artifacts/<id>/send-for-approval``). Mapping a
// UUID to an Artwork.id requires a server roundtrip on
// ``artifi_unique_name`` -- a real follow-up but out of scope for
// this fix. UUID-shape variants get a friendly client-side rejection.
function _resolveArtworkIdFromVariant(artworkFileId) {
    if (artworkFileId == null) return null;
    const raw = String(artworkFileId).trim();
    if (raw === "") return null;
    // Strict integer-string match (mirrors the backend's
    // _ARTWORK_FILE_ID_INT_RE pattern -- 1-12 digits, no leading zero).
    if (!/^[1-9][0-9]{0,11}$/.test(raw)) return null;
    return Number(raw);
}

async function onSendForApprovalRequested(payload) {
    if (!payload) return;
    // VariantCard still emits ``artifactId`` (the variant's raw
    // artwork_file_id) because the prop shape is shared with the
    // legacy library list. Resolve to an Artwork.id here.
    const resolvedArtworkId = _resolveArtworkIdFromVariant(
        payload.artifactId
    );
    if (resolvedArtworkId == null) {
        // Legacy UUID-shape variant or empty id -- no Artwork pipeline
        // target. Surface a friendly toast instead of opening the
        // modal; the user can re-bind the variant or fall back to the
        // Decoration Library send flow.
        sendToastText.value = (
            "Send-for-approval is only available for variants bound to "
            + "the new Artwork pipeline. Re-bind this variant or use "
            + "the Decoration Library to send for approval."
        );
        sendToastVisible.value = true;
        if (
            typeof window !== "undefined"
            && typeof window.setTimeout === "function"
        ) {
            window.setTimeout(() => {
                sendToastVisible.value = false;
            }, 6000);
        }
        return;
    }
    sendModalArtworkId.value = resolvedArtworkId;
    // Prefer the placement name (= decoration name in this editor's
    // model — "Front logo", "Back logo", etc.) which is what the modal
    // header surfaces to the client. The variant's filename is too
    // low-level for a per-client approval prompt. Fall back to the
    // payload's decorationName when no placement name is set.
    sendModalDecorationName.value = placementName.value
        || payload.decorationName
        || "";
    // Version: prefer the resolved Artwork record for this variant.
    const art = artworkById.value?.[payload.variantId];
    if (art && typeof art.version === "number") {
        sendModalDecorationVersion.value = art.version;
    } else {
        sendModalDecorationVersion.value = 1;
    }
    // Remember the targeted variant so a successful send can flip its
    // artwork lifecycle locally (Issue #2179 Problem 1).
    sendModalVariantId.value = payload.variantId ?? null;
    sendModalClients.value = await loadClientsForModal();
    sendToastVisible.value = false;
    sendToastText.value = "";
    sendModalOpen.value = true;
}

function onSendComplete(result) {
    // The Artwork-pipeline endpoint returns per-client results with a
    // ``status`` field (sent | already_sent | failed). Build a brief
    // summary that distinguishes the cases so the Owner sees the
    // partial-success case without opening DevTools.
    const results = Array.isArray(result?.results) ? result.results : [];

    // Issue #2179 Problem 1 (state-refresh): flip the sent variant's
    // artwork lifecycle to awaiting_approval so the card re-renders
    // "Send reminder" WITHOUT a full page reload. The backend already
    // transitioned Artwork.pipeline_state to awaiting_approval; mirror
    // that into the Pinia cache the resolver reads. Only flip when at
    // least one client actually received the send (sent | already_sent)
    // -- a wholly-failed send leaves the lifecycle untouched. No-op when
    // the variant does not resolve to a cached Artwork.
    const anyReached = results.some(
        (r) => r?.status === "sent" || r?.status === "already_sent",
    );
    if (
        (anyReached || results.length === 0)
        && sendModalVariantId.value != null
    ) {
        const sentVariant = variantById.value?.[sendModalVariantId.value];
        if (sentVariant) {
            artworks.setLifecycleStateForVariant?.(
                sentVariant,
                "awaiting_approval",
            );
        }
    }
    if (results.length === 0) {
        sendToastText.value = "Sent for approval.";
    } else {
        const sent = results.filter((r) => r.status === "sent").length;
        const already = results.filter(
            (r) => r.status === "already_sent",
        ).length;
        const failed = results.filter((r) => r.status === "failed").length;
        const parts = [];
        if (sent > 0) {
            parts.push(`Sent to ${sent} client${sent === 1 ? "" : "s"}.`);
        }
        if (already > 0) {
            parts.push(
                `${already} already had a pending approval.`,
            );
        }
        if (failed > 0) {
            parts.push(`${failed} could not be sent.`);
        }
        sendToastText.value = parts.length
            ? parts.join(" ")
            : "Sent for approval.";
    }
    sendToastVisible.value = true;
    // Auto-dismiss the toast after a few seconds; defensive guard for the
    // test environment (no window in some bundlers).
    if (typeof window !== "undefined" && typeof window.setTimeout === "function") {
        window.setTimeout(() => {
            sendToastVisible.value = false;
        }, 4000);
    }
}
</script>

<style scoped>
.deco-inspector-root {
    padding: 16px 0;
}
.deco-inspector-header {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
    display: flex;
    align-items: center;
    gap: 10px;
}
.deco-inspector-num {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--tf-bg-subtle, #eef2f6);
    color: var(--tf-text-primary, #133752);
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}
.deco-inspector-header-text {
    flex: 1 1 auto;
    min-width: 0;
}
.deco-inspector-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--tf-text-primary, #133752);
    margin: 0;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.deco-inspector-sub {
    font-size: 12px;
    color: var(--tf-text-muted, #6b7280);
    margin: 2px 0 0;
    line-height: 1.25;
}
.deco-inspector-trash {
    flex: 0 0 auto;
    background: none;
    border: none;
    color: var(--tf-text-muted, #6b7280);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    line-height: 0;
}
.deco-inspector-trash:hover {
    color: var(--tf-color-danger, #b91c1c);
    background: var(--tf-bg-danger-subtle, #fef2f2);
}
.deco-inspector-trash .material-icons {
    font-size: 20px;
}
/*
  Garment-fit warning (#2445 Track A). Two non-blocking levels:
  "advisory" reads as informational, "warning" as prominent. Both sit
  directly under the placement-details row so the size the user just
  read is adjacent to the judgment about it.
*/
.deco-inspector-fit-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0 0 12px;
    padding: 8px 10px;
    border-radius: 4px;
    border-left: 3px solid transparent;
    font-size: 12px;
    line-height: 1.45;
}
.deco-inspector-fit-warning--advisory {
    /* The info family has ONLY --tf-info (_tokens.css:42) -- no soft/dark
       member. Rather than write var(--tf-info-soft, ...) with a name that
       is defined NOWHERE (a dangling var() reads as tokenized but always
       falls through, and a raw-hex sweep skips the var() form), these two
       are plain hex and honest about it. Both values already exist raw
       elsewhere (#eff6ff PerColorApprovalBadge/InFlightOrdersNotice,
       #1e40af PortalLayout). Adding the missing tokens belongs in the
       token file, which another session is actively editing -- so it is a
       follow-up there, not a drive-by edit here. */
    background: #eff6ff;
    border-left-color: var(--tf-info, #0693e3);
    color: #1e40af;
}
.deco-inspector-fit-warning--warning {
    /* Real tokens: --tf-warning-soft / --tf-warning / --tf-warning-dark
       (_tokens.css:39-41). The earlier --tf-bg-warning-subtle /
       --tf-border-warning / --tf-text-warning names have ZERO
       definitions, so every var() fell through to a hardcoded hex --
       six effectively-raw colors wearing a token costume, which a
       raw-hex sweep would skip. Prior art: OrderDetailView.vue. */
    background: var(--tf-warning-soft, #fff8e1);
    border-left-color: var(--tf-warning, #fcb900);
    color: var(--tf-warning-dark, #8a6d3b);
    font-weight: 500;
}
.deco-inspector-fit-warning-icon {
    flex: 0 0 auto;
    margin-top: 1px;
}
.deco-inspector-fit-warning-text {
    flex: 1 1 auto;
}

.deco-inspector-location {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
}

/* The size editor wraps (limit, "was", errors): label at the top. */
.deco-inspector-size {
    align-items: flex-start;
}

.deco-inspector-size .deco-inspector-location-label {
    padding-top: 5px;
}

.deco-inspector-location-label {
    flex: none;
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-text-secondary, #374151);
}

.deco-inspector-location-select {
    flex: 1;
    min-width: 0;
}

.deco-inspector-placement-details {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin: 0 0 12px;
    padding: 8px 10px;
    background: var(--tf-bg-subtle, #f8fafc);
    border-radius: 4px;
    font-size: 12px;
    color: var(--tf-text-muted, #6b7280);
}
.deco-inspector-placement-detail + .deco-inspector-placement-detail::before {
    content: " ";
    margin-right: 4px;
    color: var(--tf-text-muted, #cbd5e1);
}
.deco-inspector-variants {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
/* Shade-artwork UX polish: split affordance under the combined card. */
.deco-inspector-split-shades {
    background: none;
    border: none;
    color: var(--tf-brand-primary, #119ae9);
    font-size: 12.5px;
    padding: 0 2px;
    cursor: pointer;
    align-self: flex-start;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.deco-inspector-split-shades:hover {
    color: var(--tf-text-primary, #133752);
}
.deco-inspector-empty {
    color: var(--tf-text-muted, #6b7280);
    font-style: italic;
    padding: 16px 0;
    margin: 0;
}
/* Merge's assumed-colorway notice. Informational, not an error: the merge
   succeeded and nothing was lost, we are only naming a choice made on the
   user's behalf. Hence the muted surface rather than the error banner's. */
.deco-inspector-merge-notice {
    margin: 8px 0 0;
    padding: 8px 10px;
    border-radius: 4px;
    background: var(--tf-bg-hover, #f3f4f6);
    color: var(--tf-text-muted, #6b7280);
    font-size: 12px;
    line-height: 1.4;
}
.deco-inspector-footer {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid var(--tf-border, #e0e0e0);
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: var(--tf-text-muted, #6b7280);
    font-size: 11.5px;
    line-height: 1.4;
}
.deco-inspector-footer-icon {
    flex: 0 0 auto;
    font-size: 14px !important;
    margin-top: 1px;
}
.deco-inspector-footer-text {
    flex: 1 1 auto;
    min-width: 0;
}
.deco-inspector-toast {
    margin-top: 12px;
    padding: 8px 12px;
    background: var(--tf-bg-success-subtle, #ecfdf5);
    color: var(--tf-success, #059669);
    border: 1px solid var(--tf-success, #059669);
    border-radius: 4px;
    font-size: 13px;
}
.deco-inspector-toast-link {
    display: block;
    margin-top: 6px;
    padding: 0;
    background: none;
    border: none;
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
}
</style>
